import { SearchOutlined } from '@ant-design/icons';
import {
  AutoComplete,
  Button,
  Col,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Tag,
  Timeline,
  Tooltip,
  notification,
} from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAllLogs } from '../../modules/trace-log/stores/slice';
import { useDispatch } from 'react-redux';
import { doGetTraceLogs } from '../../modules/trace-log/stores/thunk';
import { addDays, format } from 'date-fns';
import copy from 'copy-to-clipboard';
import { getTraceLogOrphans } from '../../modules/trace-log/api';

const DataDisplay = ({ log, type, view }) => {
  return (
    <>
      <br />
      <code>
        {JSON.stringify(log[type]).slice(0, 100)}
        {JSON.stringify(log[type]).length > 100 ? (
          <Tooltip title="Click to view all data">
            <Tag style={{ marginLeft: 16, cursor: 'pointer' }} onClick={() => view(log)}>
              ...
            </Tag>
          </Tooltip>
        ) : (
          ''
        )}
      </code>
    </>
  );
};

const DrawerTitle = ({ title, data }) => {
  const copyLog = (v) => {
    copy(v);
    notification.success({ message: 'Copied' });
  };
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      {title}
      <Button size="small" type="primary" onClick={() => copyLog(data)}>
        copy
      </Button>
    </div>
  );
};

export const TraceLogs = ({ show, preset }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const subjectId = Form.useWatch('subjectId', form);
  const subjectLongId = Form.useWatch('subjectLongId', form);
  const subjectRef = Form.useWatch('subjectRef', form);
  const subject = Form.useWatch('subject', form);
  const user = useSelector((state) => state.auth.user);
  if (!user.isAdmin) return;
  const logs = useSelector((state) => selectAllLogs(state));
  const disabled = useMemo(() => !subjectId && !subjectLongId && !subjectRef, [subjectId, subjectLongId, subjectRef]);
  const onFinish = (params) => {
    dispatch(doGetTraceLogs(params));
  };

  useEffect(() => {
    if (preset?.subjectId && preset?.subject && show) {
      dispatch(doGetTraceLogs({ subject: preset.subject, subjectId: Number(preset.subjectId) }));
      form.setFieldValue('subject', preset.subject);
      form.setFieldValue('subjectId', preset.subjectId);
    }
  }, [preset?.subjectId]);

  const [orphanRefs, setOrphanRefs] = useState([]);
  useEffect(() => {
    const getIt = async () => {
      if (subject) {
        const { response } = await getTraceLogOrphans({ since: addDays(new Date(), -7), subject });

        setOrphanRefs(
          response
            ?.map((o) => ({
              label: `${o.subjectRef} (dd: ${format(o.createdAt, 'eeee dd/MM HH:mm:ss')})`,
              value: o.subjectRef,
            }))
            .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)),
        );
      }
    };
    if (show) {
      getIt();
    }
  }, [subject]);

  const [drawerContent, setDrawerContent] = useState('');
  const view = (log) => {
    setDrawerContent({
      title: `${log.event} at ${format(log.createdAt, 'dd/MM/yyyy HH:mm:ss')}`,
      data: JSON.stringify(log.data, null, 4),
    });
  };
  const logItems = useMemo(() => {
    return logs
      .sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1))
      .map((log) => ({
        // color: statusToColor(log.status),
        children: (
          <>
            <Tag>{log.event}</Tag>
            <p>{format(log.createdAt, 'eeee MM-dd HH:mm:ss')}</p>
            {log.userId && (
              <p>
                <b>User id: </b> {log.userId}
              </p>
            )}
            {log.metaData && (
              <p>
                <b>Meta data: </b> <DataDisplay log={log} type="metaData" />
              </p>
            )}
            {log.data && (
              <p>
                <b>Data: </b>
                <DataDisplay log={log} type="data" view={view} />
              </p>
            )}
          </>
        ),
      }));
  }, [logs]);

  return (
    <div style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
      <h3>Search for:</h3>
      <Form form={form} onFinish={onFinish} initialValues={{ subject: 'ORDER' }}>
        <Form.Item label="Subject" name="subject">
          <Radio.Group name="subject">
            <Radio value="ORDER">Order</Radio>
            <Radio value="COLLO" disabled>
              Collo
            </Radio>
            <Radio value="Setting" disabled>
              Setting
            </Radio>
          </Radio.Group>
        </Form.Item>
        <Row gutter={16}>
          <Col span={4}>
            <Form.Item label="Subject id" name="subjectId">
              <InputNumber type="number" placeholder={34}></InputNumber>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Subject long id" name="subjectLongId">
              <Input placeholder="NGT1B3RJ9R"></Input>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Subject Ref" name="subjectRef">
              <AutoComplete options={orphanRefs} placeholder="O-894989" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item style={{ display: 'flex', justifyContent: 'right' }}>
          <Button type="primary" disabled={disabled} htmlType="submit">
            <SearchOutlined style={{ marginTop: 4 }} />
            Search
          </Button>
        </Form.Item>
      </Form>
      <Divider />
      {logs.length ? <Timeline items={logItems} mode="left" /> : <div>No logs to show...</div>}
      <Drawer
        title={<DrawerTitle title={drawerContent?.title} data={drawerContent?.data} />}
        placement="right"
        style={{ borderRadius: 8 }}
        width={500}
        closable={false}
        onClose={() => setDrawerContent(null)}
        open={!!drawerContent}
        getContainer={false}
      >
        <p style={{ whiteSpace: 'pre-wrap' }}>{drawerContent?.data}</p>
      </Drawer>
    </div>
  );
};
